import { Check, ShareFat, X } from "@phosphor-icons/react";
import ReactPaginate from "react-paginate";
import { useForwardMessageModalController } from "../../../controllers/modals/forwardMessageModalController";
import { Message, MessageType } from "../../../models/Message";
import { removeFormattingMarksPreservingSpacing } from "../../../utils/channelAndHtml";
import { buildInlineFileURL } from "../../../utils/fileProxy";
import { Avatar } from "../../Avatar";
import { Button, Size, Variant } from "../../Buttons";
import { IconButton, Shape } from "../../Buttons/IconButton";
import { Dialog, DialogProps } from "../../Dialog";
import { SpinLoading } from "../../SpinLoading";
import { ModalHeader } from "../styles";
import { ForwardModal, ForwardModalBody, ForwardModalView, ModalActions, Pagination, RecipientItem, RecipientList, ResultList, SearchInput, SelectionSummary, SourcePreview } from "./styles";
import { formatPhoneByDDI } from "../../../helpers/phoneHelper";

export interface ForwardMessageModalProps extends Omit<DialogProps, "children"> {
  sourceChatId: string;
  message?: Message | null;
  messages?: Array<Message>;
};

const previewMessage = ( message: Message ): string => {
  if( message.content ) return removeFormattingMarksPreservingSpacing( message.content );

  const labels: Partial<Record<MessageType, string>> = {
    [ MessageType.IMAGE ]: "Imagem",
    [ MessageType.VIDEO ]: "Vídeo",
    [ MessageType.AUDIO ]: "Áudio",
    [ MessageType.DOCUMENT ]: message.fileMetadata?.fileName || "Documento",
    [ MessageType.STICKER ]: "Figurinha",
    [ MessageType.LOCATION ]: "Localização",
    [ MessageType.LIVE_LOCATION ]: "Localização ao vivo",
    [ MessageType.CONTACT ]: "Contato",
    [ MessageType.POLL ]: "Enquete",
  };

  return labels[ message.messageType ] ?? "Mensagem";
};

export const ForwardMessageModal = ({ open, onClose = () => {}, sourceChatId, message = null, messages = [] }: ForwardMessageModalProps ) => {
  const sourceMessages = messages.length > 0 ? messages : message ? [ message ] : [];
  const primaryMessage = sourceMessages[ 0 ] ?? null;
  const controller = useForwardMessageModalController( sourceChatId, sourceMessages, open );
  const selectedIDs = new Set( controller.selectedRecipients.map(( recipient ) => recipient.id ));
  const failedCount = controller.results?.filter(( result ) => result.status === "FAILED" ).length ?? 0;

  return (
    <Dialog open={ open } onClose={ onClose } ariaLabel="Encaminhar mensagem">
      <ForwardModal>
        <ForwardModalView>
          <ModalHeader>
            <ShareFat weight="duotone" size={ 32 } />
            <span>Encaminhar mensagem</span>
            <IconButton color="error" shape={ Shape.CIRCLE } onClick={ onClose } aria-label="Fechar encaminhamento">
              <X weight="bold" />
            </IconButton>
          </ModalHeader>

          <ForwardModalBody>
            { primaryMessage && (
              <SourcePreview>
                <span>{ sourceMessages.length === 1 ? "Mensagem selecionada" : `${ sourceMessages.length } mensagens selecionadas` }</span>
                <p>{ sourceMessages.length === 1 ? previewMessage( primaryMessage ) : "As mensagens serão encaminhadas na ordem em que foram selecionadas." }</p>
              </SourcePreview>
            )}

            {
              controller.results
                ? (
                    <ResultList aria-label="Resultado do encaminhamento">
                      <h3>Resultado por contato</h3>
                      { controller.results.map(( result ) => {
                        const recipient = controller.submittedRecipients.get( result.contactId );
                        return (
                          <div key={ result.contactId } className={ result.status === "SENT" ? "success" : "failed" }>
                            <strong>{ recipient?.name || "Contato" }: { result.status === "SENT" ? "Enviada" : "Falha" }</strong>
                            { result.status === "FAILED" && <small>{ result.message || "Não foi possível encaminhar para este contato." }</small> }
                          </div>
                        );
                      })}
                    </ResultList>
                  )
                : (
                    <>
                      <SearchInput
                        aria-label="Buscar destinatários"
                        placeholder="Buscar por nome ou telefone"
                        value={ controller.search }
                        onChange={( event ) => controller.setSearch( event.target.value )}
                      />

                      <SelectionSummary>
                        <strong>{ controller.selectedRecipients.length }/20 selecionados</strong>
                        { controller.selectedRecipients.map(( recipient ) => (
                          <button key={ recipient.id } type="button" onClick={() => controller.toggleRecipient( recipient )} aria-label={ `Remover ${ recipient.name }` }>
                            { recipient.name } <X size={ 12 } />
                          </button>
                        ))}
                        { controller.selectionLimitReached && <small>Limite de 20 contatos atingido.</small> }
                      </SelectionSummary>

                      <RecipientList aria-label="Destinatários">
                        <SpinLoading show={ controller.loading } />
                        { !controller.loading && controller.recipients.length === 0 && <span>Nenhum contato encontrado.</span> }
                        { !controller.loading && controller.recipients.map(( recipient ) => {
                          const isSelected = selectedIDs.has( recipient.id );
                          return (
                            <RecipientItem
                              key={ recipient.id }
                              type="button"
                              $selected={ isSelected }
                              aria-pressed={ isSelected }
                              disabled={ !isSelected && controller.selectedRecipients.length >= 20 }
                              onClick={() => controller.toggleRecipient( recipient )}
                            >
                              <Avatar size={ 36 } name={ recipient.name } url={ buildInlineFileURL( recipient.avatarUrl ?? recipient.avatar )} />
                              <div className="contact-copy">
                                <span>{ recipient.name }</span>
                                <small>{ formatPhoneByDDI( recipient.phone )}</small>
                              </div>
                              { isSelected && <Check size={ 18 } weight="bold" /> }
                            </RecipientItem>
                          );
                        })}
                      </RecipientList>

                      { controller.pageCount > 1 && (
                        <Pagination>
                          <ReactPaginate
                            pageCount={ controller.pageCount }
                            forcePage={ controller.page - 1 }
                            onPageChange={({ selected }) => controller.setPage( selected + 1 )}
                            previousLabel="‹"
                            nextLabel="›"
                            breakLabel="…"
                          />
                        </Pagination>
                      )}
                    </>
                  )
            }

            <ModalActions>
              { controller.submitError && <span role="alert">{ controller.submitError }</span> }
              <Button $color="text" $variant={ Variant.TEXT } $size={ Size.SMALL } onClick={ onClose }>Fechar</Button>
              {
                controller.results
                  ? failedCount > 0 && (
                      <Button $color="warning" $variant={ Variant.CONTAINED } $size={ Size.SMALL } onClick={ controller.startNewOperation }>
                        Tentar falhas novamente
                      </Button>
                    )
                  : (
                      <Button
                        $color="primary"
                        $variant={ Variant.CONTAINED }
                        $size={ Size.SMALL }
                        disabled={ controller.submitting || controller.selectedRecipients.length === 0 }
                        onClick={() => void controller.submit()}
                      >
                        { controller.submitting ? "Encaminhando..." : `Encaminhar para ${ controller.selectedRecipients.length }` }
                      </Button>
                    )
              }
            </ModalActions>
          </ForwardModalBody>
        </ForwardModalView>
      </ForwardModal>
    </Dialog>
  );
};
